import {Injectable} from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import {environment} from '../../environments/environment';
import {BehaviorSubject, Observable, of} from 'rxjs';
import {Permission} from '../_models/permission';
import {catchError, map, tap} from 'rxjs/operators';
import {MatSnackBar} from '@angular/material/snack-bar';

@Injectable()
export class PermissionService {

  httpOptions = {
    headers: new HttpHeaders({
      'Content-Type':  'application/json',
    })
  };

  private source = new BehaviorSubject<Permission>(null);
  private sourcePIN = new BehaviorSubject<Permission>(null);
  private sourceOperatorPIN = new BehaviorSubject<Permission>(null);
  current = this.source.asObservable();
  pinCurrent = this.sourcePIN.asObservable();
  pinCurrentOperator = this.sourceOperatorPIN.asObservable();

  constructor(private http: HttpClient, private snackBar: MatSnackBar) { }

  getUserPermission(storeId){
    return this.http.get<Permission>(`${environment.apiUrl}/backend/v2/permission/${storeId}/current_user/`, this.httpOptions)
    .pipe(
      // A failed permission fetch (network/server error) must not leave subscribers
      // stuck on a stale/null value — log and fall through to the empty-Permission
      // fallback below, which means "no access" so gated UI simply hides instead
      // of breaking app bootstrap.
      catchError(err => {
        console.error(`Failed to load user permission for store ${storeId}`, err);
        return of(null);
      })
    )
    .subscribe(permissions => {
      if (permissions) {
        const permission = Object.assign(new Permission(), permissions);
        this.source.next(permission);
        if (this.isEmptyPermission(permission)) {
          this.snackBar.open('You don\'t have permission', 'OK', { duration: 3000 });
        }
      } else {
        this.source.next(new Permission());
        this.snackBar.open('You don\'t have permission', 'OK', { duration: 3000 });
      }
    });
  }

  // A Permission is "empty" when the user has no permissions in any group.
  private isEmptyPermission(p: Permission): boolean {
    return !p.ACCESS.length && !p.ORDERS.length && !p.POS_FUNCTION.length && !p.POS_ALERTS.length;
  }

  getUserPermissionById(storeId, userId){
    return this.http.get<Permission>(`${environment.apiUrl}/backend/v2/permission/${storeId}/`, {params: {user_id: userId}})
  }

  getUserPermissionByPIN(storeId, pin){
    return this.http.get<Permission>(`${environment.apiUrl}/backend/v2/permission/${storeId}/pin_current_user/`, {params: {pin}})
    .pipe(tap(permissions => {
      this.sourcePIN.next(Object.assign(new Permission(), permissions));
    }));
  }

  async getUserPermissionByPINSync(storeId, pin): Promise<Permission> {
    return await this.http.get<Permission>(`${environment.apiUrl}/backend/v2/permission/${storeId}/pin_current_user/`, {params: {pin}})
    .pipe(map((r: Permission) => {
      return Object.assign(new Permission(), r);
    }))
    .toPromise().then(per => {
      return per;
    });
  }

  /**
   * ENG-5974: look up the permissions behind a PIN without touching the operator / PIN
   * subjects — the tip approval dialog checks a manager's PIN, it does not sign them in.
   */
  checkPIN(storeId: number | string, pin: string): Observable<Permission> {
    return this.http.get<Permission>(`${environment.apiUrl}/backend/v2/permission/${storeId}/pin_current_user/`, {params: {pin}})
      .pipe(map((r: Permission) => Object.assign(new Permission(), r)));
  }

  getCurrentUserPermissionByPIN(storeId, pin){
    return this.http.get<Permission>(`${environment.apiUrl}/backend/v2/permission/${storeId}/pin_current_user/`, {params: {pin}})
    .pipe(tap(permissions => {
      this.sourceOperatorPIN.next(Object.assign(new Permission(), permissions));
    }));
  }

  postUserPermission(payload){
    return this.http.post(`${environment.apiUrl}/backend/v2/permission/`, payload, this.httpOptions)
  }
}
